/**
 * Algolia Utilities
 * Helper functions for Algolia integration
 */
(function() {
  'use strict';

  const config = window.algoliaThemeConfig || {};

  // Initialize global namespace for utilities
  window.algoliaUtils = {

    /**
     * Check if a product hit has the "New" tag
     * @param {Object} hit - Algolia hit object
     * @returns {boolean} True if product has "New" tag
     */
    hitIsNew: function(hit) {
      if (!hit?.tags || !Array.isArray(hit.tags)) return false;
      const newTag = config.tags?.new || 'New';
      return hit.tags.some(tag =>
        tag && tag.toLowerCase().trim() === newTag.toLowerCase().trim()
      );
    },

    /**
     * Check if a product hit has the "Free Shipping" tag
     * @param {Object} hit - Algolia hit object
     * @returns {boolean} True if product has "Free Shipping" tag
     */
    hitIsFreeShipping: function(hit) {
      if (!hit?.tags || !Array.isArray(hit.tags)) return false;
      const freeShipTag = config.tags?.freeShipping || 'Free Shipping';
      return hit.tags.some(tag =>
        tag && tag.toLowerCase().trim() === freeShipTag.toLowerCase().trim()
      );
    },

    /**
     * Check if a product is personalized
     * @param {Object} hit - Algolia hit object
     * @returns {boolean} True if product is personalized
     */
    hitIsPersonalized: function(hit) {
      if (!hit?.tags || !Array.isArray(hit.tags)) return false;
      const personalizedTags = config.tags?.personalized || ['Personalized', 'Monogram Shop'];
      return hit.tags.some(tag =>
        personalizedTags.some(pTag =>
          tag && pTag && tag.toLowerCase().trim() === pTag.toLowerCase().trim()
        )
      );
    },

    /**
     * Get quick buy button label based on product type
     * @param {Object} hit - Algolia hit object
     * @returns {string} Button label
     */
    getQuickBuyLabel: function(hit) {
      return this.hitIsPersonalized(hit)
        ? (config.text?.quickView || 'Quick View')
        : (window.algoliaShopify?.translations?.quickBuy || config.text?.quickBuy || 'Quick buy');
    },

    /**
     * Format variant title by removing parentheses and "Default Title"
     * @param {string} variantTitle - Raw variant title
     * @returns {string} Formatted variant title
     */
    formatVariantTitle: function(variantTitle) {
      if (!variantTitle) return '';

      // Remove leading "(" and trailing ")" + surrounding spaces
      let variant = variantTitle.trim()
        .replace(/^\(\s*/, '')
        .replace(/\s*\)$/, '')
        .trim();

      // Hide Shopify's "Default Title"
      if (variant.toLowerCase() === 'default title') {
        return '';
      }

      return variant;
    },

    /**
     * Check if a product is on sale
     * @param {Object} hit - Algolia hit object
     * @returns {boolean} True if on sale
     */
    isOnSale: function(hit) {
      return Number(hit.compare_at_price || 0) > Number(hit.price || 0);
    },

    /**
     * Format price with currency symbol
     * @param {number} value - Price value
     * @param {boolean} inCents - Whether price is in cents
     * @returns {string} Formatted price
     */
    formatPrice: function(value, inCents = false) {
      const priceInCents = inCents || config.search?.priceInCents || false;
      const n = priceInCents ? value / 100 : value;
      return '$' + Math.round(n).toLocaleString('en-US');
    },

    /**
     * Format price with "from" prefix for product listings
     * @param {Object} hit - Algolia hit object
     * @param {boolean} distinct - Whether using distinct variants
     * @returns {string} Formatted price with "from" prefix
     */
    formatPriceWithPrefix: function(hit, distinct) {
      if (!hit) return '';

      // Get the minimum price
      const price = hit.variants_min_price || hit.price;
      if (!price && price !== 0) return '';

      const priceInCents = config.search?.priceInCents || false;
      const formattedPrice = this.formatPrice(price, priceInCents);

      // Add "from" prefix if there's a price range (min !== max)
      const hasRange = hit.variants_min_price && hit.variants_max_price &&
                       hit.variants_min_price !== hit.variants_max_price;
      const prefix = hasRange ? 'from ' : '';

      return prefix + formattedPrice;
    },

    /**
     * Product JSON cache
     * @private
     */
    _productJsonCache: {},

    /**
     * Fetch product JSON data
     * @param {string} handle - Product handle
     * @returns {Promise<Object>} Product JSON data
     */
    getProductJson: function(handle) {
      if (!handle) {
        return Promise.reject(new Error('No product handle provided'));
      }

      if (this._productJsonCache[handle]) {
        return Promise.resolve(this._productJsonCache[handle]);
      }

      if (!window.jQuery) {
        config.error?.('jQuery not available for getProductJson');
        return Promise.reject(new Error('jQuery not available'));
      }

      const self = this;
      return window.jQuery.getJSON(`/products/${handle}.js`)
        .then(function(productData) {
          self._productJsonCache[handle] = productData;
          return productData;
        })
        .catch(function(error) {
          config.error?.('Failed to load product JSON:', handle, error);
          throw error;
        });
    },

    /**
     * Get images for a variant, with variant's featured image first
     * @param {Object} productJson - Product JSON data
     * @param {string|number} variantId - Variant ID
     * @returns {Array<string>} Array of image URLs
     */
    imagesForVariantFirst: function(productJson, variantId) {
      const imgs = Array.isArray(productJson.images) ? productJson.images : [];

      // Helper to clean image URLs (remove query params and Shopify size suffixes) for dedup
      const clean = (u) => String(u)
        .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|master|\d+x\d*|\d*x\d+)(\.(?:jpe?g|png|webp|gif))/i, '$1')
        .replace(/(\.(?:jpe?g|png|webp|gif))(?:\?.*)?$/i, '$1');

      const seen = new Set();
      const out = [];

      function push(u) {
        if (!u) return;
        const k = clean(u.src || u);
        if (!seen.has(k)) {
          seen.add(k);
          out.push(u.src || u);
        }
      }

      // Find variant and add its featured image first
      const variant = (productJson.variants || []).find(
        v => String(v.id) === String(variantId)
      );

      if (variant) {
        push(variant.featured_image?.src || variant.featured_image);
      }

      // Add product featured image
      push(productJson.featured_image);

      // Add all other images
      imgs.forEach(push);

      return out;
    },

    /**
     * Check if Slick library is available
     * @returns {boolean} True if Slick is available
     */
    hasSlickLib: function() {
      return !!(window.jQuery && window.jQuery.fn && window.jQuery.fn.slick);
    },

    /**
     * Check if an element is slick-initialized
     * @param {jQuery} $el - jQuery element
     * @returns {boolean} True if slick-initialized
     */
    isSlicked: function($el) {
      return !!($el && $el.length && $el.hasClass('slick-initialized'));
    },

    /**
     * Get slick instance from an element
     * @param {jQuery} $el - jQuery element
     * @returns {Object|null} Slick instance or null
     */
    slickInst: function($el) {
      if (!this.isSlicked($el)) return null;
      try {
        return $el.slick('getSlick');
      } catch (e) {
        config.log?.('Failed to get slick instance:', e);
        return null;
      }
    },

    /**
     * Ensure URL has protocol (add https: if starts with //)
     * @param {string} url - URL to fix
     * @returns {string} URL with protocol
     */
    ensureProtocol: function(url) {
      if (url && url.startsWith('//')) {
        return 'https:' + url;
      }
      return url;
    },

    /**
     * Check if viewport matches breakpoint
     * @param {string} breakpoint - Breakpoint name (mobile, tablet, desktop)
     * @returns {boolean} True if matches
     */
    matchesBreakpoint: function(breakpoint) {
      const bp = config.breakpoints?.[breakpoint];
      if (!bp) return false;
      return window.matchMedia(`(max-width: ${bp}px)`).matches;
    }
  };

  // Expose to global scope
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = window.algoliaUtils;
  }
})();
